// My work (/admin/work): where teammates tagged you (backlog comments and feedback internal notes), the
// backlog items you follow with comments you haven't read, the open items you own and your checklist steps.
// Opening an item or request marks its tags read; so do Mark read and Mark all read. In-app only, no email.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AtSign, Bell, CheckSquare, ListChecks, ListTodo } from 'lucide-react';
import { feedbackExcerpt, type MyOnboardingTask } from '@upm/domain';
import { api, ApiError } from '../../lib/api';
import { fmtAgo, fmtMonthDay, plural } from '../../lib/format';
import { Avatar, Banner, Button, Card, Pill, Spinner, cx, initialsOf } from '../../ui';
import { useMe } from '../AdminShell';
import { MessageBody } from '../feedback/Formatting';
import { PRIORITY_LABEL, PRIORITY_TONE, STATUS_LABEL, STATUS_TONE, whereLabel, type Item, type ItemStatus, type Priority } from '../backlog/types';

type Mention = {
  id: string; created_at: string; read_at: string | null; by_name: string | null; source: 'backlog' | 'feedback';
  target_id: string; reference: string; title: string; body: string;
};
type Step = { id: string; text: string; item_id: string; reference: string; title: string; item_status: ItemStatus };
type Followed = {
  id: string; reference: string; title: string; status: ItemStatus; priority: Priority; owner_name: string | null; updated_at: string;
  new_comments: number; last_comment: { author_name: string; body: string; created_at: string } | null;
};
type WorkData = {
  me: string; mentions: Mention[]; assigned: Item[]; steps: Step[]; following: Followed[]; counts: { mentions: number; following: number };
  /** Open tasks assigned to me in each firm's open onboarding gate (design §14). */
  onboardingTasks: MyOnboardingTask[];
};

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);
const ASSIGNED_ORDER: ItemStatus[] = ['in_progress', 'todo', 'idea'];
const linkTo = (m: Mention) => (m.source === 'backlog' ? `/admin/backlog/${m.target_id}` : `/admin/feedback/${m.target_id}`);

export function MyWorkPage() {
  const [data, setData] = useState<WorkData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allTags, setAllTags] = useState(false);
  const { reportWorkSeen } = useMe();

  const load = useCallback(async () => {
    try { setData(await api.get<WorkData>('/api/admin/work')); setError(null); }
    catch (e) { setError(msg(e, 'Could not load your work.')); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const markRead = async (ids?: string[]) => {
    try { await api.post('/api/admin/work/mentions/read', ids ? { ids } : {}); await load(); reportWorkSeen(); }
    catch (e) { setError(msg(e, 'Could not mark that read.')); }
  };
  const finishStep = async (s: Step) => {
    try { await api.patch(`/api/admin/backlog/${s.item_id}/checklist/${s.id}`, { done: true }); await load(); }
    catch (e) { setError(msg(e, 'Could not tick off that step.')); }
  };

  if (!data) return error ? <Banner tone="error" role="alert">{error}</Banner> : <Spinner label="Loading your work" />;
  const unread = data.mentions.filter((m) => !m.read_at);
  const tags = allTags ? data.mentions : unread;
  const updated = data.following.filter((f) => f.new_comments > 0);

  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h1 className="page-title">My work</h1>
        <p className="m-0 text-[13px] text-muted">
          {plural(unread.length, 'unread tag')} · {plural(updated.length, 'followed item')} with new comments · {data.assigned.length} assigned · {plural(data.steps.length, 'open step')}
        </p>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex min-w-0 flex-[999_1_540px] flex-col gap-3">
          <Section icon={AtSign} title="Tagged" count={unread.length}
            action={(
              <span className="flex items-center gap-2">
                <div role="group" aria-label="Show" className="flex gap-0.5 rounded-[10px] bg-subtle p-[3px]">
                  {[false, true].map((all) => (
                    <button key={String(all)} type="button" aria-pressed={allTags === all} onClick={() => setAllTags(all)}
                      className={cx('min-h-7 rounded-lg border-0 px-2.5 text-xs', allTags === all ? 'bg-white font-semibold text-ink shadow-seg' : 'bg-transparent font-medium text-ink-2')}>
                      {all ? 'Last 30 days' : 'Unread'}
                    </button>
                  ))}
                </div>
                {unread.length > 0 && <Button size="sm" onClick={() => void markRead()}>Mark all read</Button>}
              </span>
            )}>
            {tags.length === 0
              ? <Empty>{allTags ? 'Nobody tagged you in the last 30 days.' : 'You are all caught up. When a teammate types @ and your name in a backlog comment or an internal note, it shows here.'}</Empty>
              : (
                <ul className="m-0 flex list-none flex-col p-0">
                  {tags.map((m) => (
                    <li key={m.id} className="group flex gap-3 border-t border-line-soft py-3 first:border-t-0 first:pt-0">
                      <span aria-hidden className={cx('mt-3 h-2 w-2 flex-none rounded-full', m.read_at ? 'bg-transparent' : 'bg-brand')} />
                      <Avatar initials={initialsOf(m.by_name)} size={28} tone="soft" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px]">
                          <b>{m.by_name ?? 'Someone'}</b> tagged you {m.source === 'feedback' ? 'in an internal note on' : 'on'}{' '}
                          <Link to={linkTo(m)} className="font-semibold text-brand no-underline hover:underline">{m.reference} · {m.title}</Link>
                          <span className="text-muted"> · {fmtAgo(m.created_at)}</span>
                          {!m.read_at && <span className="sr-only"> (unread)</span>}
                        </div>
                        <MessageBody text={m.body} className="mt-1 line-clamp-3 rounded-lg bg-panel px-3 py-2 text-sm" />
                      </div>
                      {!m.read_at && (
                        <Button size="sm" variant="link" className="flex-none self-start" onClick={() => void markRead([m.id])}>Mark read</Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
          </Section>

          <Section icon={Bell} title="Following" count={updated.length}
            hint="Items you created, own, commented on or were tagged in. New comments since you last opened one show here.">
            {data.following.length === 0
              ? <Empty>You are not following any items. Open an item and choose Follow to keep up with its comments.</Empty>
              : (
                <ul className="m-0 flex list-none flex-col p-0">
                  {data.following.map((f) => (
                    <li key={f.id} className="border-t border-line-soft first:border-t-0">
                      <Link to={`/admin/backlog/${f.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-ink no-underline hover:bg-panel">
                        <span className="min-w-0 flex-[1_1_260px]">
                          <span className="text-[13px] font-semibold text-brand">{f.reference}</span>{' '}
                          <span className={cx('text-sm', f.new_comments > 0 && 'font-semibold')}>{f.title}</span>
                          {f.last_comment && (
                            <span className="block truncate text-xs text-muted">
                              {f.last_comment.author_name}: {feedbackExcerpt(f.last_comment.body, 120) || 'a screenshot'} · {fmtAgo(f.last_comment.created_at)}
                            </span>
                          )}
                        </span>
                        {f.new_comments > 0 && <Pill tone="warn">{f.new_comments} new</Pill>}
                        <Pill tone={STATUS_TONE[f.status]} dot>{STATUS_LABEL[f.status]}</Pill>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
          </Section>
        </div>

        <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-3">
          <Section icon={ListTodo} title="Onboarding tasks" count={data.onboardingTasks.length} hint="Your open tasks in each firm’s open gate. Tick them on the firm’s Gates tab.">
            {data.onboardingTasks.length === 0
              ? <Empty>No onboarding tasks are yours right now.</Empty>
              : (
                <ul className="m-0 flex list-none flex-col p-0">
                  {data.onboardingTasks.map((t) => {
                    const overdue = !!t.dueDate && t.dueDate < new Date().toISOString().slice(0, 10);
                    return (
                      <li key={t.id}>
                        <Link to={`/admin/onboarding/${t.onboardingId}#gates`} className="flex items-start gap-2 py-1.5 text-ink no-underline hover:bg-panel">
                          <span className="min-w-0 flex-1">
                            <span className="text-sm">{t.title}</span>
                            <span className="block text-xs text-muted">{t.firmName} · {t.gateTitle} gate</span>
                          </span>
                          {t.dueDate && <span className={cx('flex-none text-xs', overdue ? 'font-semibold text-bad' : 'text-ink-2')}>{overdue ? 'Overdue · ' : 'Due '}{fmtMonthDay(`${t.dueDate}T12:00:00`)}</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
          </Section>

          <Section icon={ListChecks} title="Assigned to me" count={data.assigned.length}>
            {data.assigned.length === 0
              ? <Empty>No open items are assigned to you.</Empty>
              : ASSIGNED_ORDER.map((status) => {
                const items = data.assigned.filter((i) => i.status === status);
                if (!items.length) return null;
                return (
                  <div key={status} className="flex flex-col">
                    <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">{STATUS_LABEL[status]} · {items.length}</div>
                    <ul className="m-0 flex list-none flex-col p-0">
                      {items.map((i) => (
                        <li key={i.id}>
                          <Link to={`/admin/backlog/${i.id}`} className="flex items-center gap-2 py-1.5 text-ink no-underline hover:bg-panel">
                            <span className="min-w-0 flex-1">
                              <span className="text-[13px] font-semibold text-brand">{i.reference}</span> <span className="text-sm">{i.title}</span>
                              <span className="block text-xs text-muted">{whereLabel(i)}</span>
                            </span>
                            <Pill tone={PRIORITY_TONE[i.priority]}>{PRIORITY_LABEL[i.priority]}</Pill>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
          </Section>

          <Section icon={CheckSquare} title="My checklist steps" count={data.steps.length}>
            {data.steps.length === 0
              ? <Empty>No open checklist steps are yours.</Empty>
              : (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {data.steps.map((s) => (
                    <li key={s.id} className="flex items-start gap-2 text-sm">
                      <input type="checkbox" aria-label={`Done: ${s.text}`} onChange={() => void finishStep(s)} className="mt-1 h-4 w-4 flex-none accent-brand" />
                      <span className="min-w-0">
                        {s.text}
                        <Link to={`/admin/backlog/${s.item_id}`} className="block text-xs font-medium text-muted no-underline hover:text-brand">{s.reference} · {s.title}</Link>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
          </Section>
        </aside>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, count, hint, action, children }: {
  icon: typeof AtSign; title: string; count: number; hint?: string; action?: ReactNode; children: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Icon aria-hidden size={16} className="text-ink-2" />
        <h2 className="m-0 text-[15px] font-bold">{title}</h2>
        {count > 0 && <span className="rounded-pill bg-subtle px-2 py-px text-xs font-semibold tabular-nums">{count}</span>}
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {hint && <p className="-mt-2 mb-0 text-xs text-muted">{hint}</p>}
      {children}
    </Card>
  );
}

const Empty = ({ children }: { children: ReactNode }) => <p className="m-0 text-[13px] text-muted">{children}</p>;
